import { useState, useEffect } from "react";
import { ThreeDots } from "react-loader-spinner";
import "./railRateRequestPanel.css";
import { RailRateRequestPanelProps } from "../../../types/IMissingRateRequest";
import { icons } from "../../../svg";


const RailRateRequestPanel = ({
  open,
  originName,
  origins = [],
  submitOrigins = origins,
  showOriginsSection = false,
  destinationName,
  onClose,
  onSubmit,
}: RailRateRequestPanelProps) => {
  const [businessOpportunity, setBusinessOpportunity] = useState<
    "one-time" | "long-term"
  >("one-time");
  const [volume, setVolume] = useState("");
  const [targetRate, setTargetRate] = useState("");
  const [sourcingAndLogistics, setSourcingAndLogistics] = useState("");
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkedOrigins, setCheckedOrigins] = useState<Set<string>>(
    new Set(origins),
  );

  useEffect(() => {
    setCheckedOrigins(new Set(origins));
  }, [origins]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setBusinessOpportunity("one-time");
    setVolume("");
    setTargetRate("");
    setSourcingAndLogistics("");
    setComments("");
    setCheckedOrigins(new Set(origins));
  }, [open, origins]);

  const toggleOrigin = (origin: string) => {
    setCheckedOrigins((prev) => {
      const next = new Set(prev);
      if (next.has(origin)) {
        next.delete(origin);
      } else {
        next.add(origin);
      }
      return next;
    });
  };

  if (!open) return null;

  const submit = async () => {
    if (isSubmitDisabled || isSubmitting) {
      return;
    }

    const hiddenSubmitOrigins = showOriginsSection
      ? submitOrigins.filter((origin) => !origins.includes(origin))
      : [];
    const selectedVisibleOrigins = origins.filter((o) => checkedOrigins.has(o));
    const selectedOrigins = Array.from(
      new Set([...hiddenSubmitOrigins, ...selectedVisibleOrigins]),
    );

    const payload = {
      businessOpportunity,
      volume,
      targetRate,
      sourcingAndLogistics,
      comments,
      ...(showOriginsSection && { selectedOrigins }),
    };

    setIsSubmitting(true);
    try {
      await onSubmit?.(payload);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatOriginNameDisplay = (originValue?: string) => {
    if (!originValue) {
      return "";
    }

    return originValue.replace(/[A-Za-z]+/g, (word) => {
      if (/^[A-Z]{2,3}$/.test(word)) {
        return word;
      }

      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    });
  };

  const hasOriginValue = Boolean(
    formatOriginNameDisplay(originName).trim(),
  );
  const hasTargetRate = Boolean(targetRate.trim());
  const hasSourcingAndLogistics = Boolean(sourcingAndLogistics.trim());
  const isSubmitDisabled =
    !hasOriginValue || !hasTargetRate || !hasSourcingAndLogistics;

  return (
    <div className="rail-rate-request-screen" id="rail-rate-request-screen">
      <div className="rail-rate-request-close-row">
        <button
          type="button"
          className="rail-rate-request-close-button"
          onClick={onClose}
          aria-label="Close"
        >
          <img
            src={icons.CloseBlackWhiteIcon}
            alt="Close"
            className="rail-rate-request-close-icon"
          />
        </button>
      </div>

      <div className="rail-rate-request-header-row">
        <h2 className="rail-rate-request-title">Rail Rate Request</h2>
      </div>

      <div className="rail-rate-request-content">
        <div className={showOriginsSection ? undefined : "rail-rate-request-title-block"}>
          <div className="rail-rate-request-route">
            <span className="rail-rate-request-route-endpoint">
              {showOriginsSection ? destinationName : originName}
            </span>
            {!showOriginsSection && (
              <>
                <img
                  className="rail-rate-request-route-arrow"
                  src={icons.RouteArrowIcon}
                  alt="Route Arrow"
                  style={{ margin: "0 25px" }}
                />
                <span className="rail-rate-request-route-endpoint">
                  {destinationName}
                </span>
              </>
            )}
          </div>
          {!showOriginsSection && (
            <div className="rail-rate-request-subtitle">
              No rail rate was found for this route. Please fill out this form to
              request a rail rate.
            </div>
          )}
        </div>

        <div className="rail-rate-request-form">
          {showOriginsSection && origins.length > 0 && (
            <div className="rail-rate-request-field rail-rate-request-origins-field">
              <div className="rail-rate-request-label">
                Koch Origins <span className="required">*</span>
              </div>
              <div className="rail-rate-request-helper">
                Select what origins you need a rate from.
              </div>
              <div className="rail-rate-request-origins-list">
                {origins.map((origin) => (
                  <label
                    key={origin}
                    className="rail-rate-request-radio-item"
                    htmlFor={`rail-origins-${origin}`}
                  >
                    <input
                      id={`rail-origins-${origin}`}
                      type="checkbox"
                      checked={checkedOrigins.has(origin)}
                      onChange={() => toggleOrigin(origin)}
                    />
                    <span>{origin}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="rail-rate-request-field">
            <div className="rail-rate-request-label">
              Business Opportunity <span className="required">*</span>
            </div>
            <div className="rail-rate-request-helper">
              Indicate if this is a one-time or long-term move.
            </div>
            <div className="rail-rate-request-radio-group" id="business-opportunity">
              <label className="rail-rate-request-radio-item" htmlFor="business-opportunity-one-time">
                <input
                  id="business-opportunity-one-time"
                  type="radio"
                  name="businessOpportunity"
                  checked={businessOpportunity === "one-time"}
                  onChange={() => setBusinessOpportunity("one-time")}
                />
                <span>One-time move</span>
              </label>
              <label className="rail-rate-request-radio-item" htmlFor="business-opportunity-long-term">
                <input
                  id="business-opportunity-long-term"
                  type="radio"
                  name="businessOpportunity"
                  checked={businessOpportunity === "long-term"}
                  onChange={() => setBusinessOpportunity("long-term")}
                />
                <span>Long-term</span>
              </label>
            </div>
          </div>

          <div className="rail-rate-request-field">
            <div className="rail-rate-request-label">
              Volume <span className="required">*</span>
            </div>
            <div className="rail-rate-request-helper">
              Indicate volume [carloads or tons] on this move. (Wichita - 500st;
              Dodge - 2 carloads)
            </div>
            <input
              id="rail-volume"
              aria-label="Volume"
              className="rail-rate-request-input"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
            />
          </div>

          <div className="rail-rate-request-field">
            <div className="rail-rate-request-label">
              Target Rate <span className="required">*</span>
            </div>
            <div className="rail-rate-request-helper">
              Indicate rate level required to secure the business
            </div>
            <input
              id="rail-target-rate"
              aria-label="Target Rate"
              className="rail-rate-request-input"
              value={targetRate}
              onChange={(e) => setTargetRate(e.target.value)}
            />
          </div>

          <div className="rail-rate-request-field">
            <div className="rail-rate-request-label">
              Sourcing and Logistics <span className="required">*</span>
            </div>
            <div className="rail-rate-request-helper">
              New Receiver, Current Sourcing, Alternate
            </div>
            <textarea
              id="rail-sourcing-logistics"
              aria-label="Sourcing and Logistics"
              className="rail-rate-request-textarea"
              value={sourcingAndLogistics}
              onChange={(e) => setSourcingAndLogistics(e.target.value)}
            />
          </div>

          <div className="rail-rate-request-field">
            <div className="rail-rate-request-label">
              Comments
            </div>
            <textarea
              id="rail-comments"
              aria-label="Comments"
              className="rail-rate-request-textarea"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>

        <div className="rail-rate-request-footer">
          <button
            type="button"
            className="rail-rate-request-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rail-rate-request-submit-button"
            onClick={submit}
            disabled={isSubmitDisabled || isSubmitting}
          >
            {isSubmitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ThreeDots color="#ffffff" height={16} width={28} />
                Requesting
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RailRateRequestPanel;
